import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  ContentCut as ContentCutIcon,
  EventAvailable as EventAvailableIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ImportCustomersStep from '../../components/data-import/ImportCustomersStep';
import ImportServicesStep from '../../components/data-import/ImportServicesStep';
import ImportTeamMembersStep from '../../components/data-import/ImportTeamMembersStep';
import ImportAppointmentsStep from '../../components/data-import/ImportAppointmentsStep';
import StoreDataImportService from '../../services/storeDataImportService';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const STEP_CUSTOMERS = 0;
const STEP_SERVICES = 1;
const STEP_TEAM_MEMBERS = 2;
const STEP_APPOINTMENTS = 3;

const DataImportPage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const [activeStep, setActiveStep] = useState(STEP_CUSTOMERS);
  const [progress, setProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const steps = useMemo(() => ([
    {
      label: t('dataImport.steps.customers'),
      icon: <PeopleIcon />,
    },
    {
      label: t('dataImport.steps.services'),
      icon: <ContentCutIcon />,
    },
    {
      label: t('dataImport.steps.teamMembers'),
      icon: <GroupsIcon />,
    },
    {
      label: t('dataImport.steps.appointments'),
      icon: <EventAvailableIcon />,
    },
  ]), [t]);

  const loadProgress = useCallback(async () => {
    setLoadingProgress(true);
    setLoadError(null);
    try {
      const current = await StoreDataImportService.getCurrent(storeId);
      setProgress(current);
      if (current?.customersCompleted && !current?.servicesCompleted) {
        setActiveStep(STEP_SERVICES);
      } else if (current?.servicesCompleted && !current?.teamMembersCompleted) {
        setActiveStep(STEP_TEAM_MEMBERS);
      } else if (current?.teamMembersCompleted) {
        setActiveStep(STEP_APPOINTMENTS);
      } else {
        setActiveStep(STEP_CUSTOMERS);
      }
    } catch (error) {
      setLoadError(error?.response?.data?.message || t('dataImport.errors.loadProgress'));
    } finally {
      setLoadingProgress(false);
    }
  }, [storeId, t]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const isStepUnlocked = (stepIndex) => {
    if (stepIndex === STEP_CUSTOMERS) return true;
    if (stepIndex === STEP_SERVICES) return Boolean(progress?.customersCompleted);
    if (stepIndex === STEP_TEAM_MEMBERS) return Boolean(progress?.servicesCompleted);
    if (stepIndex === STEP_APPOINTMENTS) return Boolean(progress?.teamMembersCompleted);
    return false;
  };

  const handleCustomersCompleted = async () => {
    await loadProgress();
    setActiveStep(STEP_SERVICES);
  };

  const handleServicesCompleted = async () => {
    await loadProgress();
    setActiveStep(STEP_TEAM_MEMBERS);
  };

  const handleTeamMembersCompleted = async () => {
    await loadProgress();
    setActiveStep(STEP_APPOINTMENTS);
  };

  const handleAppointmentsCompleted = async () => {
    await loadProgress();
  };

  const renderStepContent = () => {
    if (activeStep === STEP_CUSTOMERS) {
      return (
        <ImportCustomersStep
          storeId={storeId}
          onCompleted={handleCustomersCompleted}
          disabled={Boolean(progress?.customersCompleted)}
        />
      );
    }

    if (activeStep === STEP_SERVICES) {
      return (
        <ImportServicesStep
          storeId={storeId}
          onCompleted={handleServicesCompleted}
          disabled={Boolean(progress?.servicesCompleted)}
        />
      );
    }

    if (activeStep === STEP_TEAM_MEMBERS) {
      return (
        <ImportTeamMembersStep
          storeId={storeId}
          onCompleted={handleTeamMembersCompleted}
          disabled={Boolean(progress?.teamMembersCompleted)}
        />
      );
    }

    return (
      <ImportAppointmentsStep
        storeId={storeId}
        onCompleted={handleAppointmentsCompleted}
        disabled={Boolean(progress?.appointmentsCompleted)}
      />
    );
  };

  const completedAlertKey = () => {
    if (activeStep === STEP_CUSTOMERS && progress?.customersCompleted) {
      return 'dataImport.customers.alreadyCompleted';
    }
    if (activeStep === STEP_SERVICES && progress?.servicesCompleted) {
      return 'dataImport.services.alreadyCompleted';
    }
    if (activeStep === STEP_TEAM_MEMBERS && progress?.teamMembersCompleted) {
      return 'dataImport.teamMembers.alreadyCompleted';
    }
    if (activeStep === STEP_APPOINTMENTS && progress?.appointmentsCompleted) {
      return 'dataImport.appointments.alreadyCompleted';
    }
    return null;
  };

  const completedAlert = completedAlertKey();

  return (
    <ManagementPageLayout
      id="data-import-page"
      containerId="data-import-header"
      titleId="data-import-title"
      subtitleId="data-import-subtitle"
      pageTitle={t('dataImport.title')}
      pageSubtitle={t('dataImport.subtitle')}
      contentId="data-import-main-content"
      contentSx={{
        maxWidth: { lg: '960px' },
        width: { xs: '100%', lg: '960px' },
      }}
    >
      <ManagementPageCard id="data-import-card">
        <Box
          id="data-import-stepper-container"
          sx={{
            background: BRAND.background,
            borderBottom: `1px solid ${BRAND.line}`,
            p: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'stretch',
              position: 'relative',
            }}
          >
            {steps.map((step, index) => {
              const unlocked = isStepUnlocked(index);
              const completed = (
                (index === STEP_CUSTOMERS && progress?.customersCompleted)
                || (index === STEP_SERVICES && progress?.servicesCompleted)
                || (index === STEP_TEAM_MEMBERS && progress?.teamMembersCompleted)
                || (index === STEP_APPOINTMENTS && progress?.appointmentsCompleted)
              );

              return (
                <Box
                  key={step.label}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    position: 'relative',
                    overflow: 'visible',
                    justifyContent: 'flex-start',
                    opacity: unlocked ? 1 : 0.45,
                  }}
                >
                  <Box
                    id={`data-import-step-icon-${index + 1}`}
                    onClick={() => {
                      if (unlocked) setActiveStep(index);
                    }}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: activeStep >= index ? BRAND.accent : BRAND.surface,
                      color: activeStep >= index ? BRAND.onAccent : BRAND.textMuted,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem',
                      fontWeight: 'bold',
                      border: `3px solid ${activeStep >= index ? BRAND.accent : BRAND.line}`,
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      cursor: unlocked ? 'pointer' : 'default',
                      zIndex: 2,
                      position: 'relative',
                    }}
                  >
                    {completed ? <CheckIcon /> : step.icon}
                  </Box>
                  <Typography
                    id={`data-import-step-label-${index + 1}`}
                    sx={{
                      color: activeStep >= index ? BRAND.text : BRAND.textMuted,
                      fontWeight: activeStep >= index ? 700 : 500,
                      fontSize: '0.9rem',
                      mt: 1.5,
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'block' },
                      lineHeight: '1.25rem',
                      width: '100%',
                      flex: 1,
                    }}
                  >
                    {step.label}
                  </Typography>
                  {index < steps.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '25px',
                        left: 'calc(50% + 25px)',
                        right: 'calc(-50% + 25px)',
                        height: '3px',
                        background: activeStep > index ? BRAND.accent : BRAND.line,
                        zIndex: 1,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box id="data-import-step-content" sx={{ p: { xs: 2, sm: 4, md: 6 } }}>
          {loadError && (
            <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('dataImport.languageNotice')}
          </Alert>

          {completedAlert && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(completedAlert)}
            </Alert>
          )}

          {loadingProgress ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: BRAND.accent }} />
            </Box>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.2 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          )}

          <Box
            id="data-import-navigation"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 4,
              pt: 3,
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <Button
              id="data-import-back-button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              disabled={activeStep === STEP_CUSTOMERS}
              onClick={() => setActiveStep((current) => Math.max(STEP_CUSTOMERS, current - 1))}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                borderColor: BRAND.accent,
                color: BRAND.accentText,
                fontWeight: 600,
                '&:hover': {
                  borderColor: BRAND.accentDark,
                  backgroundColor: brandAlpha('accent', 0.1),
                },
                '&:disabled': {
                  borderColor: brandAlpha('accent', 0.3),
                  color: brandAlpha('accent', 0.3),
                },
              }}
            >
              {t('common.back')}
            </Button>
            <Button
              id="data-import-next-button"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={!isStepUnlocked(activeStep + 1)}
              onClick={() => setActiveStep((current) => Math.min(STEP_APPOINTMENTS, current + 1))}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                background: BRAND.accent,
                color: BRAND.onAccent,
                boxShadow: 'none',
                '&:hover': {
                  background: BRAND.accentDark,
                  boxShadow: 'none',
                },
                '&:disabled': {
                  background: '#ccc',
                  transform: 'none',
                  boxShadow: 'none',
                },
              }}
            >
              {t('common.next')}
            </Button>
          </Box>
        </Box>
      </ManagementPageCard>
    </ManagementPageLayout>
  );
};

export default DataImportPage;
